import {
  PartialDateField,
  ActionBanner,
  ActionRowCard,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  FieldMessage,
  PrimaryButton,
  MoonNumericField,
  SecondaryButton,
  IconButton,
  Row,
  semanticColors,
  TextField,
} from "@louise/moon-ui";
import { monthYearToStartDate } from "@src/packages/cycles/presenters/monthYearInput";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  ClipboardList,
  Snowflake,
  Sparkles,
  Syringe,
  type LucideIcon,
} from "lucide-react-native";
import { ScrollView, View, useWindowDimensions } from "react-native";
import { useCanonicalCycleIntake } from "@src/packages/care/clinical-record";
import {
  CYCLE_TYPE_LABEL_KEYS,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import {
  resolveEndCycleFamily,
  resolveEndCycleOptions,
  type EndCycleOptionId,
} from "../YouScreen/modals/endCycleOptions";
import type { CanonicalCycleDraft } from "@src/packages/care/clinical-record/useCanonicalCycleIntake";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import pastCycleIllustration from "../../../assets/images/past-cycle-dialog-illustration.png";

type EntryStep =
  | "type"
  | "when"
  | "treatment_outcome"
  | "pregnancy_outcome"
  | "pregnancy_details"
  | "outcome"
  | "outcome_followup"
  | "details";

type TreatmentOutcome = "completed" | "cancelled" | "other";
type PregnancyResult = "pregnancy" | "no_pregnancy" | "other";
type PregnancyDetails =
  "ongoing" | "live_birth" | "multiple_live_birth" | "loss" | "other";

type PastCycleOutcome = Exclude<EndCycleOptionId, "completed">;

type MetricKey =
  | "follicles"
  | "eggsRetrieved"
  | "matureEggs"
  | "eggsFrozen"
  | "fertilizedEggs"
  | "day3Embryos"
  | "blastocysts"
  | "embryosFrozen"
  | "embryosTransferred";

type MetricValues = Record<MetricKey, number | null>;

type CyclePhaseDraft = NonNullable<
  CanonicalCycleDraft["phases"]
>[number] extends infer Phase
  ? Phase extends { fertilityMetrics?: infer Metrics }
    ? Metrics extends readonly object[]
      ? Phase
      : never
    : never
  : never;
type CycleFertilityMetric = NonNullable<
  CyclePhaseDraft["fertilityMetrics"]
>[number] extends infer Metric
  ? Metric extends { type?: infer MetricType }
    ? MetricType extends string
      ? Metric
      : never
    : never
  : never;

const CYCLE_TYPES: CycleType[] = [
  "ivf",
  "icsi",
  "iui",
  "fet",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
];

const EMPTY_METRICS: MetricValues = {
  follicles: null,
  eggsRetrieved: null,
  matureEggs: null,
  eggsFrozen: null,
  fertilizedEggs: null,
  day3Embryos: null,
  blastocysts: null,
  embryosFrozen: null,
  embryosTransferred: null,
};

const METRIC_LABEL_KEYS: Record<MetricKey, string> = {
  follicles: "care.all_past_cycles.metric_follicles",
  eggsRetrieved: "care.all_past_cycles.metric_eggs_retrieved",
  matureEggs: "care.all_past_cycles.metric_mature_eggs",
  eggsFrozen: "care.all_past_cycles.metric_eggs_frozen",
  fertilizedEggs: "care.all_past_cycles.metric_fertilized_eggs",
  day3Embryos: "care.all_past_cycles.metric_day3_embryos",
  blastocysts: "care.all_past_cycles.metric_blastocysts",
  embryosFrozen: "care.all_past_cycles.metric_embryos_frozen",
  embryosTransferred: "care.all_past_cycles.metric_embryos_transferred",
};

// The API uses the generated frozen_embryo metric type for both frozen eggs
// and frozen embryos; the cycle phase and the field's cycle-specific meaning
// distinguish the two preservation counts.
const METRIC_TYPE_BY_KEY: Partial<
  Record<MetricKey, NonNullable<CycleFertilityMetric["type"]>>
> = {
  follicles: "follicles_over_17_mm",
  eggsRetrieved: "retrieved_oocytes",
  matureEggs: "m2_count",
  eggsFrozen: "frozen_embryo",
  fertilizedEggs: "fertilized_oocytes",
  day3Embryos: "embryos_day_3",
  blastocysts: "embryos_day_5",
  embryosFrozen: "frozen_embryo",
  embryosTransferred: "transferred_embryo",
};

const RETRIEVAL_KEYS: MetricKey[] = [
  "follicles",
  "eggsRetrieved",
  "matureEggs",
];

function cycleTypeGroup(cycleType: CycleType | null) {
  if (cycleType === "egg_freezing") return "egg_freezing" as const;
  if (cycleType === "embryo_freezing") return "embryo_freezing" as const;
  if (cycleType === "sperm_freezing") return "sperm_freezing" as const;
  if (cycleType === "ivf" || cycleType === "icsi") return "ivf" as const;
  if (cycleType === "fet") return "fet" as const;
  return "outcome_only" as const;
}

function supportsPregnancyOutcome(cycleType: CycleType | null): boolean {
  return (
    cycleType != null &&
    cycleTypeGroup(cycleType) !== "egg_freezing" &&
    cycleTypeGroup(cycleType) !== "embryo_freezing" &&
    cycleTypeGroup(cycleType) !== "sperm_freezing"
  );
}

function metricKeysForCycle(cycleType: CycleType | null): MetricKey[] {
  switch (cycleTypeGroup(cycleType)) {
    case "egg_freezing":
      return [...RETRIEVAL_KEYS, "eggsFrozen"];
    case "embryo_freezing":
      return [...RETRIEVAL_KEYS, "fertilizedEggs", "embryosFrozen"];
    case "ivf":
      return [
        ...RETRIEVAL_KEYS,
        "fertilizedEggs",
        "blastocysts",
        "embryosTransferred",
      ];
    case "fet":
      return ["embryosTransferred"];
    default:
      return [];
  }
}

function phaseTypeForMetric(
  cycleType: CycleType | null,
  key: MetricKey,
): NonNullable<CyclePhaseDraft["type"]> {
  if (
    key === "follicles" ||
    key === "eggsRetrieved" ||
    key === "matureEggs" ||
    (key === "eggsFrozen" && cycleType === "egg_freezing")
  ) {
    return "egg_retrieval";
  }
  if (key === "fertilizedEggs") return "fertilization";
  if (key === "embryosTransferred") return "transfer";
  return "embryo_development";
}

function buildPhaseDrafts(
  cycleType: CycleType | null,
  metrics: MetricValues,
  startDate: string,
): CanonicalCycleDraft["phases"] {
  const keys = metricKeysForCycle(cycleType);
  const grouped = new Map<NonNullable<CyclePhaseDraft["type"]>, MetricKey[]>();

  for (const key of keys) {
    if (metrics[key] == null) continue;
    const type = phaseTypeForMetric(cycleType, key);
    grouped.set(type, [...(grouped.get(type) ?? []), key]);
  }

  const phases: NonNullable<CanonicalCycleDraft["phases"]> = [];
  for (const [type, phaseKeys] of grouped) {
    const fertilityMetrics: NonNullable<CyclePhaseDraft["fertilityMetrics"]> =
      phaseKeys.map((key) => ({
        type: METRIC_TYPE_BY_KEY[key]!,
        value: metrics[key],
      }));
    phases.push({
      type,
      startDate: startDate || null,
      endDate: null,
      fertilityMetrics,
    });
  }

  return phases.length > 0 ? phases : undefined;
}

function metricMax(key: MetricKey, values: MetricValues): number {
  switch (key) {
    case "matureEggs":
      return values.eggsRetrieved ?? 999;
    case "eggsFrozen":
      return values.matureEggs ?? values.eggsRetrieved ?? 999;
    case "fertilizedEggs":
      return values.matureEggs ?? values.eggsRetrieved ?? 999;
    case "day3Embryos":
      return values.fertilizedEggs ?? 999;
    case "blastocysts":
    case "embryosFrozen":
    case "embryosTransferred":
      return values.blastocysts ?? values.fertilizedEggs ?? 999;
    default:
      return 999;
  }
}

function optionIcon(id: EndCycleOptionId): LucideIcon {
  if (id === "completed") return CheckCircle2;
  if (id === "cancelled") return CircleAlert;
  if (id === "pregnancy") return Sparkles;
  return ClipboardList;
}

function cycleTypeIcon(cycleType: CycleType): LucideIcon {
  if (
    cycleType === "egg_freezing" ||
    cycleType === "embryo_freezing" ||
    cycleType === "sperm_freezing"
  ) {
    return Snowflake;
  }
  if (cycleType === "fet") return Sparkles;
  return Syringe;
}

/**
 * One choice in a step: the canonical Row with its icon and a check on the
 * current choice. Choosing does not move on by itself (Next does), so there
 * is no chevron. The input fill keeps the dialog's scroll area from clipping
 * a glass row's shadow into a grey band.
 */
function ChoiceRow({
  icon: Icon,
  title,
  subtitle,
  selected,
  action,
  onPress,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  selected: boolean;
  action: string;
  onPress: () => void;
}) {
  return (
    <Row
      surface="input"
      leading={
        <Icon
          size={20}
          color={semanticColors.text.secondary}
          strokeWidth={1.5}
        />
      }
      leadingSize={24}
      title={title}
      subtitle={subtitle}
      chevron={false}
      trailing={
        selected ? (
          <Check
            size={18}
            color={semanticColors.text.primary}
            strokeWidth={2.2}
          />
        ) : undefined
      }
      // Rows with a description stay compact so a step fits without scrolling.
      paddingVertical={subtitle ? 9 : undefined}
      accessibilityLabel={title}
      accessibilityState={{ selected }}
      action={action}
      onPress={onPress}
    />
  );
}

function StepDots({
  current,
  total,
  accessibilityLabel,
}: {
  current: number;
  total: number;
  accessibilityLabel: string;
}) {
  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel}
      style={{
        alignItems: "center",
        marginBottom: 20,
      }}
    >
      <View style={{ flexDirection: "row", gap: 6 }}>
        {Array.from({ length: total }).map((_, index) => (
          <View
            key={index}
            style={{
              width: index === current ? 18 : 6,
              height: 6,
              borderRadius: 3,
              backgroundColor:
                index === current
                  ? EMBER.colors.warm
                  : index < current
                    ? "rgba(184, 74, 22, 0.35)"
                    : "rgba(0, 0, 0, 0.06)",
            }}
          />
        ))}
      </View>
    </View>
  );
}

export function PastCycleEntryDialog({
  visible,
  onClose,
  onOutcomeDetailsRequested,
}: {
  visible: boolean;
  onClose: () => void;
  onOutcomeDetailsRequested: (input: {
    cycleId: string;
    cycleType: CycleType;
    /**
     * The ending the patient already chose here, handed over unwritten.
     *
     * The beta modal authors the outcome itself, so writing it here too would
     * leave the cycle with two current records and therefore no readable
     * outcome. But the patient has already answered how the cycle ended, and
     * abandoning the modal must not discard that answer — so the caller keeps it
     * and records it if the modal closes without one.
     */
    outcome?: CanonicalCycleDraft["outcome"];
  }) => void;
}) {
  const t = useTranslations();
  const { height: windowHeight } = useWindowDimensions();
  const { save: saveCanonicalCycle, isPending: isSaving } =
    useCanonicalCycleIntake();
  const [step, setStep] = useState<EntryStep>("type");
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [cycleType, setCycleType] = useState<CycleType | null>(null);
  const [outcome, setOutcome] = useState<EndCycleOptionId | null>(null);
  const [treatmentOutcome, setTreatmentOutcome] =
    useState<TreatmentOutcome | null>(null);
  const [pregnancyResult, setPregnancyResult] =
    useState<PregnancyResult | null>(null);
  const [pregnancyDetails, setPregnancyDetails] =
    useState<PregnancyDetails | null>(null);
  const [metrics, setMetrics] = useState<MetricValues>(EMPTY_METRICS);
  const [monthYear, setMonthYear] = useState("");
  const [notes, setNotes] = useState("");
  // A failed save is about the whole cycle, so it shows as a banner. Every
  // other message answers the current step's question and sits under it.
  const [formError, setFormErrorState] = useState<{
    message: string;
    isSaveFailure: boolean;
  } | null>(null);
  const setFormError = (message: string | null) =>
    setFormErrorState(
      message === null ? null : { message, isSaveFailure: false },
    );
  const scrollRef = useRef<ScrollView>(null);

  const cycleTypeOptions = useMemo(
    () =>
      CYCLE_TYPES.map((value) => ({
        value,
        label: t(CYCLE_TYPE_LABEL_KEYS[value] ?? value),
      })),
    [t],
  );
  const preservationOutcomeOptions = useMemo(() => {
    if (!cycleType) return [];
    return resolveEndCycleOptions({
      family: resolveEndCycleFamily({ cycleType, treatmentGoal: null }),
      context: "past_cycle_outcome",
    }).options;
  }, [cycleType]);
  const metricKeys = metricKeysForCycle(cycleType);
  const pregnancyOutcomeFlow = supportsPregnancyOutcome(cycleType);
  const showTreatmentMetrics = !pregnancyOutcomeFlow && metricKeys.length > 0;

  const progressSteps: EntryStep[] = pregnancyOutcomeFlow
    ? [
        "type",
        "when",
        "treatment_outcome",
        ...(treatmentOutcome === "cancelled" || treatmentOutcome === "other"
          ? ["details" as const]
          : [
              "pregnancy_outcome" as const,
              ...(pregnancyResult === "pregnancy"
                ? ["pregnancy_details" as const, "outcome_followup" as const]
                : pregnancyResult === "no_pregnancy"
                  ? ["outcome_followup" as const]
                  : ["details" as const]),
            ]),
      ]
    : ["type", "when", "outcome", "details"];

  const treatmentOutcomeOptions = [
    {
      id: "completed" as const,
      labelKey: "care.all_past_cycles.treatment_completed",
      descriptionKey: "care.all_past_cycles.treatment_completed_description",
    },
    {
      id: "cancelled" as const,
      labelKey: "care.all_past_cycles.treatment_cancelled",
      descriptionKey: "care.all_past_cycles.treatment_cancelled_description",
    },
    {
      id: "other" as const,
      labelKey: "care.all_past_cycles.treatment_other",
      descriptionKey: "care.all_past_cycles.treatment_other_description",
    },
  ];

  const pregnancyResultOptions = [
    {
      id: "pregnancy" as const,
      labelKey: "care.all_past_cycles.pregnancy_including_loss",
      descriptionKey:
        "care.all_past_cycles.pregnancy_including_loss_description",
    },
    {
      id: "no_pregnancy" as const,
      labelKey: "you.modals.end_cycle.outcome_no_pregnancy",
      descriptionKey: "you.modals.end_cycle.no_pregnancy_outcome_description",
    },
    {
      id: "other" as const,
      labelKey: "care.all_past_cycles.pregnancy_result_not_recorded",
      descriptionKey:
        "care.all_past_cycles.pregnancy_result_not_recorded_description",
    },
  ];

  const pregnancyDetailsOptions = [
    {
      id: "ongoing" as const,
      labelKey: "care.all_past_cycles.pregnancy_ongoing",
      descriptionKey: "care.all_past_cycles.pregnancy_ongoing_description",
    },
    {
      id: "live_birth" as const,
      labelKey: "care.all_past_cycles.pregnancy_live_birth",
      descriptionKey: "care.all_past_cycles.pregnancy_live_birth_description",
    },
    {
      id: "multiple_live_birth" as const,
      labelKey: "care.all_past_cycles.pregnancy_multiple_live_birth",
      descriptionKey:
        "care.all_past_cycles.pregnancy_multiple_live_birth_description",
    },
    {
      id: "loss" as const,
      labelKey: "care.all_past_cycles.pregnancy_loss",
      descriptionKey: "care.all_past_cycles.pregnancy_loss_description",
    },
    {
      id: "other" as const,
      labelKey: "care.all_past_cycles.pregnancy_details_other",
      descriptionKey:
        "care.all_past_cycles.pregnancy_details_other_description",
    },
  ];

  useEffect(() => {
    if (!visible) return;
    setStep("type");
    setCycleId(null);
    setCycleType(null);
    setOutcome(null);
    setTreatmentOutcome(null);
    setPregnancyResult(null);
    setPregnancyDetails(null);
    setMetrics(EMPTY_METRICS);
    setMonthYear("");
    setNotes("");
    setFormErrorState(null);
  }, [visible]);

  useEffect(() => {
    const resetTimer = setTimeout(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, 60);
    return () => clearTimeout(resetTimer);
  }, [step]);

  const selectCycleType = (next: CycleType) => {
    setCycleType(next);
    setOutcome(null);
    setTreatmentOutcome(null);
    setPregnancyResult(null);
    setPregnancyDetails(null);
    setMetrics(EMPTY_METRICS);
  };

  /**
   * `includeOutcome` is false on the one path that hands the outcome to the beta
   * modal instead. Writing it here as well would leave the cycle with two
   * current outcome records — and the API resolves a cycle with two current
   * records to no outcome at all, so the past cycle would read as merely
   * "Completed" no matter what the patient recorded.
   */
  const buildDraft = ({
    includeDetails,
    includeOutcome,
  }: {
    includeDetails: boolean;
    includeOutcome: boolean;
  }): CanonicalCycleDraft => {
    const startDate = monthYearToStartDate(monthYear);
    const draft: CanonicalCycleDraft = {
      cycleId,
      cycleType,
      status: "closed",
      startDate: startDate ?? undefined,
      description: notes.trim() || undefined,
    };

    const selectedOutcome = pregnancyOutcomeFlow
      ? treatmentOutcome == null
        ? null
        : treatmentOutcome === "cancelled"
          ? "cancelled"
          : treatmentOutcome === "other"
            ? "other"
            : pregnancyResult == null
              ? null
              : pregnancyResult === "no_pregnancy"
                ? "no_pregnancy"
                : pregnancyResult === "other"
                  ? "other"
                  : pregnancyDetails === "loss"
                    ? "miscarriage"
                    : "pregnancy"
      : outcome;

    if (includeOutcome && selectedOutcome && selectedOutcome !== "completed") {
      draft.outcome = {
        type: selectedOutcome as PastCycleOutcome,
        ...(pregnancyOutcomeFlow
          ? {
              pregnancyOutcome:
                pregnancyDetails === "live_birth"
                  ? "live_birth"
                  : pregnancyDetails === "multiple_live_birth"
                    ? "multiple_live_birth"
                    : null,
              miscarriageOutcome: null,
            }
          : {}),
        dateRecorded: startDate,
        notes: notes.trim() || null,
      };
    }

    if (includeDetails) {
      draft.phases = buildPhaseDrafts(cycleType, metrics, startDate ?? "");
    }

    return draft;
  };

  const persist = async (options: {
    includeDetails: boolean;
    includeOutcome: boolean;
  }): Promise<string | null> => {
    if (!cycleType) {
      setFormError(t("care.all_past_cycles.validation_type_required"));
      return null;
    }
    setFormError(null);
    try {
      const result = await saveCanonicalCycle(buildDraft(options));
      setCycleId(result.cycle.id);
      return result.cycle.id;
    } catch (error) {
      setFormErrorState({
        message:
          error instanceof Error
            ? error.message
            : t("care.all_past_cycles.save_error"),
        isSaveFailure: true,
      });
      return null;
    }
  };

  const handleContinue = async () => {
    if (step === "type") {
      if (!cycleType) {
        setFormError(t("care.all_past_cycles.validation_type_required"));
        return;
      }
      setStep("when");
      return;
    }
    if (step === "when") {
      if (!monthYearToStartDate(monthYear)) {
        setFormError(t("care.all_past_cycles.validation_month_year_required"));
        return;
      }
      setFormError(null);
      setStep(pregnancyOutcomeFlow ? "treatment_outcome" : "outcome");
      return;
    }
    if (step === "treatment_outcome") {
      if (!treatmentOutcome) {
        setFormError(
          t("care.all_past_cycles.validation_treatment_outcome_required"),
        );
        return;
      }
      setFormError(null);
      setStep(
        treatmentOutcome === "completed" ? "pregnancy_outcome" : "details",
      );
      return;
    }
    if (step === "pregnancy_outcome") {
      if (!pregnancyResult) {
        setFormError(
          t("care.all_past_cycles.validation_pregnancy_result_required"),
        );
        return;
      }
      setFormError(null);
      setStep(
        pregnancyResult === "pregnancy"
          ? "pregnancy_details"
          : pregnancyResult === "no_pregnancy"
            ? "outcome_followup"
            : "details",
      );
      return;
    }
    if (step === "pregnancy_details") {
      if (!pregnancyDetails) {
        setFormError(
          t("care.all_past_cycles.validation_pregnancy_details_required"),
        );
        return;
      }
      setFormError(null);
      setStep("outcome_followup");
      return;
    }
    if (step === "outcome") {
      if (!outcome) {
        setFormError(t("care.all_past_cycles.validation_outcome_required"));
        return;
      }
      setFormError(null);
      setStep("details");
      return;
    }
    if (await persist({ includeDetails: true, includeOutcome: true })) {
      onClose();
    }
  };

  const handleSaveCycle = async () => {
    if (!monthYearToStartDate(monthYear)) {
      setFormError(t("care.all_past_cycles.validation_month_year_required"));
      return;
    }
    if (await persist({ includeDetails: false, includeOutcome: true })) {
      onClose();
    }
  };

  const handleAddOutcomeDetails = async () => {
    // The beta modal this opens authors the outcome itself, with the provenance
    // and the numeric source a typed result needs. Saving the dialog's own
    // outcome first would only give it a second record to collide with.
    const savedCycleId = await persist({
      includeDetails: false,
      includeOutcome: false,
    });
    if (!savedCycleId || !cycleType) return;
    // Built rather than written: whoever opens the modal keeps it, so closing the
    // modal without recording a result still keeps the ending already chosen here.
    const chosenOutcome = buildDraft({
      includeDetails: false,
      includeOutcome: true,
    }).outcome;
    onOutcomeDetailsRequested({
      cycleId: savedCycleId,
      cycleType,
      outcome: chosenOutcome,
    });
    onClose();
  };

  const handleBack = () => {
    if (isSaving) return;
    if (step === "type") {
      onClose();
      return;
    }
    setStep(
      step === "details"
        ? pregnancyOutcomeFlow
          ? treatmentOutcome === "completed"
            ? pregnancyResult === "other"
              ? "pregnancy_outcome"
              : "treatment_outcome"
            : "treatment_outcome"
          : "outcome"
        : step === "outcome_followup"
          ? pregnancyResult === "pregnancy"
            ? "pregnancy_details"
            : "pregnancy_outcome"
          : step === "pregnancy_details"
            ? "pregnancy_outcome"
            : step === "pregnancy_outcome"
              ? "treatment_outcome"
              : step === "treatment_outcome"
                ? "when"
                : step === "outcome"
                  ? "when"
                  : "type",
    );
  };

  const stepTitle =
    step === "type"
      ? t("care.all_past_cycles.type_step_title")
      : step === "when"
        ? t("care.all_past_cycles.when_step_title")
        : step === "treatment_outcome"
          ? t("care.all_past_cycles.treatment_outcome_step_title")
          : step === "pregnancy_outcome"
            ? t("care.all_past_cycles.pregnancy_outcome_step_title")
            : step === "pregnancy_details"
              ? t("care.all_past_cycles.pregnancy_details_step_title")
              : step === "outcome"
                ? t("care.all_past_cycles.outcome_step_title")
                : step === "outcome_followup"
                  ? t("care.all_past_cycles.outcome_followup_title")
                  : t("care.all_past_cycles.details_step_title");
  const stepSubtitle =
    step === "type"
      ? t("care.all_past_cycles.type_step_subtitle")
      : step === "when"
        ? t("care.all_past_cycles.when_step_subtitle")
        : step === "treatment_outcome"
          ? t("care.all_past_cycles.treatment_outcome_step_subtitle")
          : step === "pregnancy_outcome"
            ? t("care.all_past_cycles.pregnancy_outcome_step_subtitle")
            : step === "pregnancy_details"
              ? t("care.all_past_cycles.pregnancy_details_step_subtitle")
              : step === "outcome"
                ? t("care.all_past_cycles.outcome_step_subtitle")
                : step === "outcome_followup"
                  ? t("care.all_past_cycles.outcome_followup_subtitle")
                  : showTreatmentMetrics
                    ? t("care.all_past_cycles.details_step_subtitle")
                    : t("care.all_past_cycles.outcome_note_step_subtitle");
  const dialogMaxHeight = Math.max(
    480,
    Math.min(760, Math.max(360, windowHeight - 32)),
  );

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      size="wide"
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isSaving}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{ maxHeight: dialogMaxHeight }}
      testID="past-cycle-entry-dialog"
    >
      {step !== "type" ? (
        <IconButton
          accessibilityLabel={t("care.all_past_cycles.back_button")}
          action="care.all_past_cycles.back_button"
          onPress={() => handleBack()}
          hitSlop={8}
          size="sm"
          surface="bare"
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
          }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </IconButton>
      ) : null}

      <View style={{ flex: 1, minHeight: 0 }}>
        <ScrollView
          key={step}
          ref={scrollRef}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ paddingBottom: 12 }}
        >
          <DialogIcon source={pastCycleIllustration} width={78} height={78} />
          <View style={{ alignItems: "center", marginTop: 10 }}>
            <StepDots
              current={Math.max(0, progressSteps.indexOf(step))}
              total={progressSteps.length}
              accessibilityLabel={t("care.all_past_cycles.step_progress", {
                params: {
                  current: String(Math.max(0, progressSteps.indexOf(step)) + 1),
                  total: String(progressSteps.length),
                },
              })}
            />
            <DialogHeader
              align="center"
              title={stepTitle}
              subtitle={stepSubtitle}
              style={{ width: "100%" }}
              subtitleStyle={{ textAlign: "center", width: "100%" }}
            />
          </View>

          {step === "type" ? (
            <View style={{ gap: 8, marginTop: 20 }}>
              {cycleTypeOptions.map((option) => (
                <ChoiceRow
                  key={option.value}
                  icon={cycleTypeIcon(option.value)}
                  title={option.label}
                  selected={cycleType === option.value}
                  action={`past_cycle_type_${option.value}`}
                  onPress={() => selectCycleType(option.value)}
                />
              ))}
            </View>
          ) : null}

          {step === "when" ? (
            <View style={{ marginTop: 20 }}>
              <PartialDateField
                label={t("care.all_past_cycles.when_month_year_label")}
                labelIcon={CalendarDays}
                precision="month"
                value={monthYear}
                onChange={(value) => {
                  setMonthYear(value);
                  setFormError(null);
                }}
              />
            </View>
          ) : null}

          {step === "treatment_outcome" ? (
            <View style={{ gap: 8, marginTop: 20 }}>
              {treatmentOutcomeOptions.map((option) => {
                const selected = treatmentOutcome === option.id;
                return (
                  <ChoiceRow
                    key={option.id}
                    icon={optionIcon(option.id)}
                    title={t(option.labelKey)}
                    subtitle={t(option.descriptionKey)}
                    selected={selected}
                    action={`past_cycle_treatment_outcome_${option.id}`}
                    onPress={() => {
                      const next = selected ? null : option.id;
                      setTreatmentOutcome(next);
                      if (next !== "completed") {
                        setPregnancyResult(null);
                        setPregnancyDetails(null);
                      }
                      setFormError(null);
                    }}
                  />
                );
              })}
            </View>
          ) : null}

          {step === "pregnancy_outcome" ? (
            <View style={{ gap: 8, marginTop: 20 }}>
              {pregnancyResultOptions.map((option) => {
                const selected = pregnancyResult === option.id;
                return (
                  <ChoiceRow
                    key={option.id}
                    icon={optionIcon(option.id)}
                    title={t(option.labelKey)}
                    subtitle={t(option.descriptionKey)}
                    selected={selected}
                    action={`past_cycle_pregnancy_result_${option.id}`}
                    onPress={() => {
                      const next = selected ? null : option.id;
                      setPregnancyResult(next);
                      if (next !== "pregnancy") setPregnancyDetails(null);
                      setFormError(null);
                    }}
                  />
                );
              })}
            </View>
          ) : null}

          {step === "pregnancy_details" ? (
            <View style={{ gap: 8, marginTop: 20 }}>
              {pregnancyDetailsOptions.map((option) => {
                const selected = pregnancyDetails === option.id;
                return (
                  <ChoiceRow
                    key={option.id}
                    icon={
                      option.id === "loss"
                        ? CircleAlert
                        : option.id === "ongoing"
                          ? Sparkles
                          : CheckCircle2
                    }
                    title={t(option.labelKey)}
                    subtitle={t(option.descriptionKey)}
                    selected={selected}
                    action={`past_cycle_pregnancy_details_${option.id}`}
                    onPress={() => {
                      setPregnancyDetails(selected ? null : option.id);
                      setFormError(null);
                    }}
                  />
                );
              })}
            </View>
          ) : null}

          {step === "outcome" ? (
            <View style={{ gap: 8, marginTop: 20 }}>
              {preservationOutcomeOptions.map((option) => {
                const selected = outcome === option.id;
                return (
                  <ChoiceRow
                    key={option.id}
                    icon={optionIcon(option.id)}
                    title={t(option.labelKey)}
                    subtitle={t(option.descriptionKey)}
                    selected={selected}
                    action={`past_cycle_outcome_${option.id}`}
                    onPress={() => setOutcome(selected ? null : option.id)}
                  />
                );
              })}
            </View>
          ) : null}

          {step === "outcome_followup" ? (
            <View style={{ marginTop: 20 }}>
              <ActionRowCard
                leading={<Sparkles size={18} color={EMBER.colors.textMid} />}
                title={t(
                  "care.all_past_cycles.outcome_details_available_title",
                )}
                subtitle={t(
                  "care.all_past_cycles.outcome_details_available_subtitle",
                )}
                action="care.all_past_cycles.add_outcome_details_button"
                onPress={handleAddOutcomeDetails}
                accessibilityLabel={t(
                  "care.all_past_cycles.add_outcome_details_button",
                )}
              />
            </View>
          ) : null}

          {step === "details" ? (
            <View style={{ gap: 16, marginTop: 20 }}>
              {showTreatmentMetrics ? (
                <View style={{ gap: 10 }}>
                  {Array.from({ length: Math.ceil(metricKeys.length / 2) }).map(
                    (_, rowIndex) => {
                      const rowKeys = metricKeys.slice(
                        rowIndex * 2,
                        rowIndex * 2 + 2,
                      );
                      return (
                        <View
                          key={`metric-row-${rowIndex}`}
                          style={{ flexDirection: "row", gap: 10 }}
                        >
                          {rowKeys.map((key) => (
                            <View key={key} style={{ flex: 1, minWidth: 0 }}>
                              <MoonNumericField
                                label={t(METRIC_LABEL_KEYS[key])}
                                value={metrics[key]}
                                onChange={(value) =>
                                  setMetrics((current) => ({
                                    ...current,
                                    [key]: value,
                                  }))
                                }
                                min={0}
                                max={metricMax(key, metrics)}
                                disabled={isSaving}
                              />
                            </View>
                          ))}
                        </View>
                      );
                    },
                  )}
                </View>
              ) : null}

              <TextField
                label={t("care.all_past_cycles.notes_label")}
                value={notes}
                onChangeText={setNotes}
                placeholder={t("care.all_past_cycles.notes_placeholder")}
                numberOfLines={3}
                notes
              />
            </View>
          ) : null}

          {formError?.isSaveFailure ? (
            <ActionBanner
              variant="important"
              message={formError.message}
              style={{ marginTop: 14 }}
            />
          ) : formError ? (
            <FieldMessage style={{ marginTop: 14 }}>
              {formError.message}
            </FieldMessage>
          ) : null}
        </ScrollView>

        <DialogActions style={{ marginTop: 18 }}>
          <SecondaryButton
            label={
              step === "when" || step === "outcome_followup"
                ? t("care.all_past_cycles.save_cycle_button")
                : t("care.all_past_cycles.back_button")
            }
            onPress={
              step === "when" || step === "outcome_followup"
                ? handleSaveCycle
                : handleBack
            }
            disabled={
              isSaving ||
              ((step === "when" || step === "outcome_followup") &&
                !monthYearToStartDate(monthYear))
            }
            flex={1}
          />
          <PrimaryButton
            label={
              step === "details"
                ? t("care.all_past_cycles.save_button")
                : step === "when"
                  ? t("care.all_past_cycles.add_details_button")
                  : step === "outcome_followup"
                    ? t("care.all_past_cycles.add_outcome_details_button")
                    : t("care.all_past_cycles.continue_button")
            }
            onPress={
              step === "outcome_followup"
                ? handleAddOutcomeDetails
                : handleContinue
            }
            loading={isSaving}
            disabled={
              isSaving ||
              (step === "type" && !cycleType) ||
              (step === "when" && !monthYearToStartDate(monthYear)) ||
              (step === "treatment_outcome" && !treatmentOutcome) ||
              (step === "pregnancy_outcome" && !pregnancyResult) ||
              (step === "pregnancy_details" && !pregnancyDetails) ||
              (step === "outcome" && !outcome)
            }
            flex={1}
          />
        </DialogActions>
      </View>
    </Dialog>
  );
}
